import * as React from "react";
import { Send, Sparkles } from "lucide-react";
import { useCopilot } from "@/context/CopilotContext";
import { Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SUGGESTED_PROMPTS, answerCopilotPrompt } from "@/lib/copilotEngine";
import { cn } from "@/lib/cn";

interface ConversationEntry {
  id: string;
  prompt: string;
  answer: string;
}

export function AICopilotPanel() {
  const { open, setOpen, pendingPrompt, clearPendingPrompt } = useCopilot();
  const [entries, setEntries] = React.useState<ConversationEntry[]>([]);
  const [input, setInput] = React.useState("");
  const [thinking, setThinking] = React.useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const counterRef = React.useRef(0);

  const submit = React.useCallback((prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    setThinking(true);
    setInput("");
    window.setTimeout(() => {
      counterRef.current += 1;
      const answer = answerCopilotPrompt(trimmed);
      setEntries((prev) => [...prev, { id: `qa-${counterRef.current}`, prompt: trimmed, answer }]);
      setThinking(false);
    }, 480);
  }, []);

  React.useEffect(() => {
    if (pendingPrompt) {
      submit(pendingPrompt);
      clearPendingPrompt();
    }
  }, [pendingPrompt, submit, clearPendingPrompt]);

  React.useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [entries, thinking]);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent side="right" widthClassName="w-full max-w-md">
        <DrawerHeader>
          <div className="flex items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-lg bg-brand-muted text-brand">
              <Sparkles className="size-4" />
            </span>
            <DrawerTitle>AI Copilot</DrawerTitle>
          </div>
          <DrawerDescription>Ask about clients, revenue, risk, or your team — grounded in live portfolio data.</DrawerDescription>
        </DrawerHeader>

        <DrawerBody ref={scrollRef} className="flex flex-col gap-4">
          {entries.length === 0 && !thinking && (
            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Try asking</p>
              <div className="flex flex-col gap-2">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => submit(prompt)}
                    className="rounded-lg border border-border bg-accent/40 px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:border-brand/40 hover:bg-brand-muted/60"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {entries.map((entry) => (
            <div key={entry.id} className="flex flex-col gap-2">
              <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-brand px-3.5 py-2 text-sm text-brand-foreground">
                {entry.prompt}
              </div>
              <div className="mr-auto flex max-w-[92%] gap-2">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-muted text-brand">
                  <Sparkles className="size-3.5" />
                </span>
                <div className="whitespace-pre-line rounded-2xl rounded-tl-sm border border-border bg-card px-3.5 py-2.5 text-sm text-foreground">
                  {entry.answer}
                </div>
              </div>
            </div>
          ))}

          {thinking && (
            <div className="mr-auto flex max-w-[92%] gap-2">
              <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-muted text-brand">
                <Sparkles className="size-3.5" />
              </span>
              <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm border border-border bg-card px-4 py-3">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={cn("size-1.5 animate-bounce rounded-full bg-muted-foreground")}
                    style={{ animationDelay: `${i * 0.12}s` }}
                  />
                ))}
              </div>
            </div>
          )}
        </DrawerBody>

        <DrawerFooter className="flex-col items-stretch gap-2">
          <form
            className="flex w-full items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              submit(input);
            }}
          >
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit(input);
                }
              }}
              placeholder="Ask the Copilot anything about the business…"
              className="min-h-10 flex-1 resize-none"
              rows={1}
            />
            <Button type="submit" size="icon" disabled={input.trim().length === 0}>
              <Send className="size-4" />
            </Button>
          </form>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
